import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PageHero, SectionLabel } from "../components/site/Bits";
import Reveal from "../components/site/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import { IMAGES, VALUES } from "../data/content";

const CHAPTERS = [
  {
    no: "01",
    title: "Our Story",
    body:
      "At Mark Clean Services Ltd, we believe every customer deserves a clean home and a spotless vehicle without the stress. We are committed to providing reliable, affordable and high-quality domestic cleaning and mobile car valeting services throughout Preston and the surrounding areas.",
  },
  {
    no: "02",
    title: "Our Mission",
    body:
      "To provide professional cleaning services that exceed customer expectations while delivering excellent value and outstanding customer care — every home, every vehicle, every time.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        crumb="About Us"
        label="About Us"
        title="A cleaner home and a spotless car — without the stress."
        subtitle="Local, insured and genuinely reliable. Here's what drives Mark Clean Services Ltd."
        image={IMAGES.story}
      />

      {/* Chapters */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 space-y-20 lg:space-y-28">
          {CHAPTERS.map((c) => (
            <Reveal key={c.no}>
              <div className="grid gap-6 lg:grid-cols-12 lg:gap-12 items-start">
                <div className="lg:col-span-4">
                  <span className="font-heading font-extrabold text-brandgreen text-7xl lg:text-8xl tracking-tighter leading-none">
                    {c.no}
                  </span>
                  <h2 className="mt-3 font-heading font-extrabold text-navy text-3xl lg:text-4xl tracking-tighter">
                    {c.title}
                  </h2>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-lg lg:text-2xl text-navy/80 leading-relaxed font-light tracking-tight">
                    {c.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee dark words={VALUES} />

      {/* Values */}
      <section className="py-24 lg:py-32 bg-mist">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><SectionLabel>03 — Our Values</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-2xl">
              The principles behind every clean.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-black/5 rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="bg-white p-8 hover:bg-navy hover:text-white transition-colors duration-300 group"
              >
                <span className="text-xs text-brandgreen font-semibold">{`0${i + 1}`}</span>
                <p className="mt-3 font-heading font-bold text-xl tracking-tight">{v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-navy text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-xl">
            Experience the Mark Clean difference.
          </h2>
          <Link
            to="/book"
            data-testid="about-cta-book"
            className="inline-flex items-center gap-2 rounded-full bg-brandgreen px-8 py-4 font-semibold hover:bg-white hover:text-navy transition-colors duration-300 shrink-0"
          >
            Book Online <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
